/**
 * Client Snapshot — periodic structured log emission for automated debug queries.
 *
 * Emits a `[ClientSnapshot]` system log every 30s containing per-symbol health
 * (refCount, age, last price). Piggybacks on existing remote log shipping
 * (whitelist-gated), so only users enabled in `debug_logging_users` MongoDB
 * collection actually ship snapshots.
 *
 * Consumed server-side by GET /api/debug/clientsnapshot?user={login} which
 * reads the latest `[ClientSnapshot]` entry from `client_debug_logs`.
 *
 * Safe to start unconditionally — logBatcher gates internally on whitelist.
 */

import marketdata from "../data/Services/marketdata";
import logger from "./logger";

const SNAPSHOT_INTERVAL_MS = 120000;
const STALE_THRESHOLD_SEC = 30;

let intervalId = null;

function takeSnapshot() {
  try {
    if (!marketdata.subscriptionManager) return;

    const now = Date.now();
    const subs = marketdata.subscriptionManager.debug();

    // Skip when idle — no symbols subscribed means nothing useful to report,
    // avoids polluting MongoDB with empty snapshots while the user has the
    // client open but no charts/watchlists registered.
    if (!subs || subs.length === 0) return;

    const symbols = subs.map((s) => {
      const sym = s.canonical || s.symbol;
      const quote = marketdata.lastQuotes?.get(sym);
      const receivedAt = quote?._receivedAt;
      const ageSec = receivedAt ? (now - receivedAt) / 1000 : null;
      const dt = quote?.DT ? new Date(quote.DT).toISOString() : null;
      let status;
      if (ageSec == null) status = "NO_DATA";
      else if (ageSec > STALE_THRESHOLD_SEC) status = "STALE";
      else status = "OK";
      return {
        symbol: sym,
        refCount: s.refCount,
        subscribers: s.subscribers,
        ageSec,
        last: quote?.C ?? null,
        dt,
        receivedAt: receivedAt ? new Date(receivedAt).toISOString() : null,
        status,
      };
    });

    const receivers = Array.isArray(marketdata.receivers)
      ? marketdata.receivers
      : [];
    const receiverHandlers = receivers.map((r) => {
      const handlers = [];
      if (typeof r?.OnQuote === "function") handlers.push("OnQuote");
      if (typeof r?.OnBar === "function") handlers.push("OnBar");
      return handlers.join("+") || "unknown";
    });

    logger.diagnostic.info("[ClientSnapshot]", {
      user: marketdata.user,
      broker: marketdata.broker,
      connectionId: marketdata.signalRConnection?.connectionId ?? null,
      state: marketdata.state,
      workerAlive: !!marketdata.quoteWorker,
      receiversCount: receivers.length,
      receiverHandlers,
      lastQuoteCacheSize: marketdata.lastQuotes?.size ?? 0,
      totalSymbols: symbols.length,
      symbols,
    });
  } catch (err) {
    console.warn("[ClientSnapshot] failed to take snapshot", err);
  }
}

export function startClientSnapshot() {
  if (intervalId) return;
  intervalId = setInterval(takeSnapshot, SNAPSHOT_INTERVAL_MS);
  takeSnapshot();
}

export function stopClientSnapshot() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
